import React, { useState, useEffect } from 'react';
import { db, auth } from '../../utils/firebase';
import { collection, addDoc, getDocs, query } from 'firebase/firestore';
import { Container, Typography, TextField, Button, Card, CardContent, Box, Alert, Divider, Select, MenuItem, FormControl, InputLabel, Paper, Chip, Table, TableBody, TableRow, TableCell } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';

const ExpenseSplitter = () => {
  const [expenses, setExpenses] = useState([]);
  const [title, setTitle] = useState('');
  const [amount, setAmount] = useState('');
  const [splitBy, setSplitBy] = useState('');
  const [selectedParticipants, setSelectedParticipants] = useState([]);
  const [ratios, setRatios] = useState({});
  const [amounts, setAmounts] = useState({});
  const [notes, setNotes] = useState('');
  const [availableTrips, setAvailableTrips] = useState([]);
  const [selectedTrip, setSelectedTrip] = useState('');
  const [currentTripParticipants, setCurrentTripParticipants] = useState([]);
  const [error, setError] = useState('');
  const module = 'expense-splitter';

  // Helper functions for consistent formatting
  const formatName = (name) => {
    if (!name) return '';
    return name.toLowerCase().split(' ').map(word =>
      word.charAt(0).toUpperCase() + word.slice(1)
    ).join(' ');
  };

  useEffect(() => {
    fetchTrips();
  }, []);

  const fetchTrips = async () => {
    try {
      // First, find the current user's document in the users collections
      let userDocId = null;
      try {
        // Try expense-splitter module first
        const expenseQuery = query(collection(db, 'modules/expense-splitter/users'));
        const expenseSnapshot = await getDocs(expenseQuery);
        const expenseUser = expenseSnapshot.docs.find(doc => doc.data().email === auth.currentUser?.email);
        if (expenseUser) {
          userDocId = expenseUser.id;
        } else {
          // Try trip-manager module
          const tripQuery = query(collection(db, 'modules/trip-manager/users'));
          const tripSnapshot = await getDocs(tripQuery);
          const tripUser = tripSnapshot.docs.find(doc => doc.data().email === auth.currentUser?.email);
          if (tripUser) {
            userDocId = tripUser.id;
          }
        }
      } catch (err) {
        // Error finding user document - continue without userDocId
      }

      const q = query(collection(db, 'modules/trip-manager/trips'));
      const querySnapshot = await getDocs(q);
      const data = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));

      // Filter trips where current user is participant
      const userTrips = data.filter(trip => {
        return trip.participants.some(p => {
          // Check multiple matching criteria
          const emailMatch = p.email === auth.currentUser?.email;
          const idMatch = p.id === auth.currentUser?.uid;
          const userDocMatch = userDocId && p.id === userDocId;

          return emailMatch || idMatch || userDocMatch;
        });
      });

      setAvailableTrips(userTrips);
    } catch (err) {
      console.error('Failed to load trips:', err);
      setError('Failed to load trips.');
    }
  };

  const handleTripChange = (tripId) => {
    setError(''); // Clear any previous errors when changing trips
    setSelectedTrip(tripId);
    setExpenses([]);
    setSelectedParticipants([]);
    const trip = availableTrips.find(t => t.id === tripId);
    setCurrentTripParticipants(trip ? trip.participants : []);
    if (tripId) {
      fetchSplits(tripId);
    }
  };

  const fetchSplits = async (tripId) => {
    try {
      const q = query(collection(db, `modules/${module}/trips/${tripId}/expenses`));
      const querySnapshot = await getDocs(q);
      const data = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setExpenses(data);
    } catch (err) {
      setError('Failed to load expenses.');
    }
  };

  const updateParticipant = (index, value) => {
    const newParticipants = [...selectedParticipants];
    newParticipants[index] = value;
    setSelectedParticipants(newParticipants);
  };

  const removeParticipant = (index) => {
    const newParticipants = selectedParticipants.filter((_, i) => i !== index);
    setSelectedParticipants(newParticipants);
    if (splitBy === 'ratio') {
      const newRatios = { ...ratios };
      delete newRatios[selectedParticipants[index]];
      setRatios(newRatios);
    } else if (splitBy === 'amount') {
      const newAmounts = { ...amounts };
      delete newAmounts[selectedParticipants[index]];
      setAmounts(newAmounts);
    }
  };

  const updateRatio = (participant, value) => {
    setRatios({ ...ratios, [participant]: parseFloat(value) || 0 });
  };

  const updateAmount = (participant, value) => {
    setAmounts({ ...amounts, [participant]: parseFloat(value) || 0 });
  };

  const addParticipant = () => {
    setSelectedParticipants([...selectedParticipants, '']);
  };

  const validateForm = () => {
    if (!title || title.length > 100) return 'Title is required and must be up to 100 characters.';
    if (!amount || parseFloat(amount) <= 0) return 'Amount must be a positive number.';
    if (!splitBy) return 'Split By is required.';
    if (selectedParticipants.length === 0 || selectedParticipants.some(p => !p)) return 'At least one participant must be selected.';
    if (splitBy === 'ratio') {
      const totalRatio = Object.values(ratios).reduce((sum, r) => sum + r, 0);
      if (totalRatio === 0) return 'Ratios must sum to more than 0.';
    }
    if (splitBy === 'amount') {
      const totalAmount = Object.values(amounts).reduce((sum, a) => sum + a, 0);
      if (totalAmount !== parseFloat(amount)) return 'Amounts must sum to the total amount.';
    }
    return null;
  };

  const calculateSplits = () => {
    const amt = parseFloat(amount);
    const splits = {};
    if (splitBy === 'equally') {
      const share = amt / selectedParticipants.length;
      selectedParticipants.forEach(p => splits[p] = share);
    } else if (splitBy === 'ratio') {
      const totalRatio = Object.values(ratios).reduce((sum, r) => sum + r, 0);
      selectedParticipants.forEach(p => splits[p] = (ratios[p] || 0) / totalRatio * amt);
    } else if (splitBy === 'amount') {
      selectedParticipants.forEach(p => splits[p] = amounts[p] || 0);
    }
    return splits;
  };

  const addExpense = async () => {
    setError(''); // Clear any previous errors
    const validationError = validateForm();
    if (validationError) {
      setError(validationError);
      return;
    }
    const splits = calculateSplits();
    const newExpense = {
      title,
      amount: parseFloat(amount),
      splitBy,
      participants: selectedParticipants,
      splits,
      ratios: splitBy === 'ratio' ? ratios : null,
      amounts: splitBy === 'amount' ? amounts : null,
      notes,
      submittedBy: auth.currentUser?.displayName || auth.currentUser?.email,
      createdAt: new Date()
    };
    try {
      await addDoc(collection(db, `modules/${module}/trips/${selectedTrip}/expenses`), newExpense);
      setTitle('');
      setAmount('');
      setSplitBy('');
      setSelectedParticipants([]);
      setRatios({});
      setAmounts({});
      setNotes('');
      fetchSplits(selectedTrip);
    } catch (err) {
      setError('Failed to add expense.');
    }
  };

  return (
    <Container maxWidth="md" sx={{ mt: 4 }}>
      <Typography variant="h5" gutterBottom>Expense Splitter</Typography>
      <Typography variant="body1" sx={{ mb: 2 }}>
        Split expenses fairly among trip participants. First select a trip, then add expenses for that trip.
      </Typography>
      <Divider sx={{ mb: 4 }} />

      {/* Trip Selection Section */}
      {!selectedTrip ? (
        <Card sx={{ mb: 4 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>Select a Trip</Typography>
            <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
              Choose a trip to start splitting expenses. You'll only see trips you're a participant in.
            </Typography>
            <FormControl fullWidth required>
              <InputLabel>Select Trip</InputLabel>
              <Select value={selectedTrip} onChange={(e) => handleTripChange(e.target.value)}>
                {availableTrips.map((trip) => (
                  <MenuItem key={trip.id} value={trip.id}>{trip.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </CardContent>
        </Card>
      ) : (
        <>
          {/* Selected Trip Display */}
          <Paper
            elevation={2}
            sx={{
              p: 3,
              mb: 4,
              borderRadius: 3,
              background: 'linear-gradient(135deg, #E0F6FF 0%, #F0F8FF 100%)', // Light blue gradient
              border: '2px solid',
              borderColor: 'primary.main',
            }}
          >
            <Typography variant="h6" sx={{ color: 'primary.main', fontWeight: 'bold', mb: 1 }}>
              Current Trip: {availableTrips.find(t => t.id === selectedTrip)?.name}
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
              {availableTrips.find(t => t.id === selectedTrip)?.description || 'No description available'}
            </Typography>
            <Typography variant="subtitle2" sx={{ color: 'secondary.main', mb: 1 }}>
              Participants ({currentTripParticipants.length}):
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
              {currentTripParticipants.map((participant) => (
                <Chip
                  key={participant.id}
                  label={formatName(participant.name)}
                  size="small"
                  variant="outlined"
                  sx={{
                    borderRadius: 2,
                    borderColor: 'primary.main',
                    color: 'primary.main',
                    backgroundColor: 'primary.50',
                    alignSelf: 'flex-start',
                  }}
                />
              ))}
            </Box>
            <Button
              variant="contained"
              color="primary"
              size="small"
              onClick={() => setSelectedTrip('')}
              startIcon={<SwapHorizIcon />}
              sx={{ mt: 2, fontSize: '0.7rem', px: 1.5, py: 0.5 }}
            >
              Change Trip
            </Button>
          </Paper>

          {/* Expense Form */}
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          <Card sx={{ mb: 4 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>Add Expense</Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <TextField
                  label="Title"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  fullWidth
                  inputProps={{ maxLength: 100 }}
                  required
                />
                <TextField
                  label="Amount (INR)"
                  type="number"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  fullWidth
                  required
                />
                <FormControl fullWidth required>
                  <InputLabel>Split By</InputLabel>
                  <Select value={splitBy} onChange={(e) => setSplitBy(e.target.value)}>
                    <MenuItem value="equally">Equal Parts</MenuItem>
                    <MenuItem value="ratio">Ratio</MenuItem>
                    <MenuItem value="amount">Exact Amount</MenuItem>
                  </Select>
                </FormControl>
            <Box>
              <Typography variant="subtitle1">Split Among</Typography>
              {selectedParticipants.map((participant, index) => (
                <Paper
                  key={index}
                  elevation={1}
                  sx={{
                    p: 2,
                    mb: 2,
                    borderRadius: 2,
                    border: '1px solid',
                    borderColor: 'divider',
                    backgroundColor: 'background.paper'
                  }}
                >
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    {/* Participant Selection Row */}
                    <FormControl fullWidth>
                      <InputLabel>Participant</InputLabel>
                      <Select value={participant} onChange={(e) => updateParticipant(index, e.target.value)}>
                        {currentTripParticipants.map((user) => (
                          <MenuItem key={user.id} value={user.name}>{formatName(user.name)}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>

                    {/* Container for ratio/amount fields and remove button */}
                    <Box sx={{ position: 'relative' }}>
                      {/* Ratio/Amount Row (only for ratio and amount splits) */}
                      {(splitBy === 'ratio' || splitBy === 'amount') && (
                        <Box sx={{ mb: 1 }}>
                          {splitBy === 'ratio' && (
                            <TextField
                              label="Ratio"
                              type="number"
                              value={ratios[participant] || ''}
                              onChange={(e) => updateRatio(participant, e.target.value)}
                              fullWidth
                              inputProps={{ min: 0, step: 0.1 }}
                            />
                          )}
                          {splitBy === 'amount' && (
                            <TextField
                              label="Amount (₹)"
                              type="number"
                              value={amounts[participant] || ''}
                              onChange={(e) => updateAmount(participant, e.target.value)}
                              fullWidth
                              inputProps={{ min: 0, step: 0.01 }}
                            />
                          )}
                        </Box>
                      )}

                      {/* Remove Button positioned at bottom right */}
                      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: splitBy === 'equally' ? 0 : 1 }}>
                        <Button
                          variant="outlined"
                          color="error"
                          size="small"
                          onClick={() => removeParticipant(index)}
                          startIcon={<DeleteIcon />}
                          sx={{
                            borderRadius: 1,
                            fontSize: '0.65rem',
                            px: 1.5,
                            py: 0.25,
                            '&:hover': {
                              backgroundColor: 'error.main',
                              color: 'white',
                            }
                          }}
                        >
                          Remove
                        </Button>
                      </Box>
                    </Box>
                  </Box>
                </Paper>
              ))}
              <Button
                variant="outlined"
                onClick={addParticipant}
                color="primary"
                size="small"
                startIcon={<PersonAddIcon />}
                sx={{
                  borderRadius: 2,
                  fontSize: '0.65rem',
                  px: 1.5,
                  py: 0.5,
                  '&:hover': {
                    backgroundColor: 'primary.main',
                    color: 'white',
                  }
                }}
              >
                Add Participant
              </Button>
            </Box>
            <TextField
              label="Notes (optional)"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              fullWidth
              multiline
              rows={3}
            />
            <Button variant="contained" color="primary" onClick={addExpense}>Add Expense</Button>
          </Box>
        </CardContent>
      </Card>
      <Typography variant="h6" gutterBottom>View Added Expenses</Typography>
      <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
        Review and manage all expenses for the selected trip. 
      </Typography>
      <Divider sx={{ mb: 3 }} />
      {expenses.length === 0 ? (
        <Typography variant="body1" sx={{ textAlign: 'center', color: 'text.secondary', py: 4 }}>
          No expenses added yet. Add your first expense above!
        </Typography>
      ) : (
        <Card elevation={2} sx={{ mb: 4 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom sx={{ color: 'primary.main', fontWeight: 'bold' }}>
              Expenses for {availableTrips.find(t => t.id === selectedTrip)?.name}
            </Typography>
            <Table sx={{ borderCollapse: 'collapse' }}>
              <TableBody>
          {expenses
            .sort((a, b) => new Date(b.createdAt?.toDate?.() || b.createdAt) - new Date(a.createdAt?.toDate?.() || a.createdAt))
                .map((exp, index) => (
                <TableRow key={exp.id} sx={{ border: 'none' }}>
                  <TableCell sx={{ border: 'none', padding: 0 }}>
                    <Box
                      sx={{
                        py: 2,
                        px: 1,
                        backgroundColor: 'white',
                        width: '100%',
                        borderBottom: '2px solid transparent',
                        borderImage: 'repeating-linear-gradient(90deg, #4A90E2 0px, #4A90E2 3px, transparent 3px, transparent 6px, #4A90E2 6px, #4A90E2 8px, transparent 8px, transparent 12px, #4A90E2 12px, #4A90E2 15px, transparent 15px, transparent 18px) 1',
                        mb: 2,
                      }}
                    >
                <Typography variant="h6" gutterBottom sx={{ color: '#1b5e20', fontWeight: 'bold' }}>
                  {exp.title}
                </Typography>

                <Typography variant="body1" sx={{ color: 'primary.main', fontWeight: 'bold', mb: 2 }}>
                  ₹{parseFloat(exp.amount).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </Typography>

                <Divider sx={{ mb: 1, borderColor: 'divider' }} />

                <Box sx={{ mb: 1 }}>
                  <Typography variant="subtitle2" sx={{ mb: 0.5, color: 'secondary.main', fontWeight: 'medium' }}>
                    Split Details ({exp.splitBy}):
                  </Typography>
                  {Object.entries(exp.splits).map(([participant, amount]) => (
                    <Typography key={participant} variant="body2" sx={{ color: 'text.secondary' }}>
                      {formatName(participant)} | ₹{parseFloat(amount).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </Typography>
                  ))}
                </Box>

                <Divider sx={{ mb: 1, borderColor: 'divider' }} />
                <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1 }}>
                  Notes: {exp.notes || "None"}
                </Typography>

                <Box sx={{ mt: 'auto', pt: 1 }}>
                  <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>
                    Submitted By: {formatName(exp.submittedBy) || 'Unknown'}
                  </Typography>
                  <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>
                    Created: {exp.createdAt?.toDate?.()
                      ? exp.createdAt.toDate().toLocaleDateString()
                      : new Date(exp.createdAt).toLocaleDateString()
                    }
                  </Typography>
                  <Chip
                    label={exp.splitBy.charAt(0).toUpperCase() + exp.splitBy.slice(1)}
                    size="small"
                    variant="outlined"
                    sx={{
                      borderRadius: 2,
                      borderColor: 'warning.main',
                      color: 'warning.main',
                      backgroundColor: 'warning.50',
                      fontWeight: 'medium',
                      mt: 1,
                    }}
                  />
                </Box>
                </Box>
                  </TableCell>
                </TableRow>
              ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}
    </>
    )}
    </Container>
  );
};

export default ExpenseSplitter;